import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { SystemStyleObject } from "@chakra-ui/system";
import { useQueryClient } from "@tanstack/react-query";
import {
  getFeatureFlagRetrieveQueryKey,
  useFeatureFlagRetrieveSuspense,
} from "gen/endpoints/feature-flags/feature-flags";
import {
  getInternalFeatureFlagCustomerTableDataRetrieveQueryKey,
  getInternalFeatureFlagUserTableDataRetrieveQueryKey,
  useInternalFeatureFlagCustomerTableDataRetrieveSuspense,
  useInternalFeatureFlagUpdate,
  useInternalFeatureFlagUserTableDataRetrieveSuspense,
} from "gen/endpoints/internal/internal";
import { FeatureFlagReadData } from "gen/model/featureFlagReadData";
import { FeatureFlagStatusTypeEnum } from "gen/model/featureFlagStatusTypeEnum";
import { FeatureFlagTypeEnum } from "gen/model/featureFlagTypeEnum";
import { InternalFeatureFlagEditDataRequest } from "gen/model/internalFeatureFlagEditDataRequest";
import { get } from "lodash-es";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  ConfirmationDialogProps,
  useConfirmationDialog,
} from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { RowSelectionState } from "shared/components/table/data/DataTableTypes";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { naiveFormatEnumValue } from "shared/utils/formattingUtils";

const tableSx: SystemStyleObject = {
  ".ag-row.ag-row-selected": {
    "--ag-background-color": "var(--chakra-colors-primary-50)",
    "--ag-odd-row-background-color": "var(--chakra-colors-primary-50)",
    ".ag-checkbox-input-wrapper": {
      borderColor: "primary.700",
      backgroundColor: "primary.25",
      _after: {
        bgColor: "primary.700",
      },
    },
  },
};

type InternalFeatureFlagEnabledStatusType = "environment" | "custom" | "none";

const getFeatureFlagStatusType = (
  featureFlagData: FeatureFlagReadData | undefined,
): InternalFeatureFlagEnabledStatusType => {
  if (!featureFlagData?.enabled) {
    return "none";
  }

  switch (featureFlagData.flag_type) {
    case FeatureFlagTypeEnum.environment:
      return "environment";
    case FeatureFlagTypeEnum.override:
      return "custom";
    default:
      return "none";
  }
};

export const InternalFeatureFlagCustomerTable: React.FC<{
  featureFlagId: string;
  rowSelection: RowSelectionState;
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>;
}> = ({ featureFlagId, rowSelection, setRowSelection }) => {
  const { t } = useTranslation(["common", "internal"]);
  const { data: customerTableData } =
    useInternalFeatureFlagCustomerTableDataRetrieveSuspense(featureFlagId, {
      only_include_enabled: !setRowSelection,
    });

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>
          {t("internal:featureFlagPage.enabledCustomers")}
        </TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={customerTableData}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          sx={tableSx}
        />
      </TableBoxBody>
    </TableBox>
  );
};

export const InternalFeatureFlagUserTable: React.FC<{
  featureFlagId: string;
  rowSelection: RowSelectionState;
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>;
}> = ({ featureFlagId, rowSelection, setRowSelection }) => {
  const { t } = useTranslation(["common", "internal"]);
  const { data: userTableData } =
    useInternalFeatureFlagUserTableDataRetrieveSuspense(featureFlagId, {
      only_include_enabled: !setRowSelection,
    });

  return (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>
          {t("internal:featureFlagPage.enabledUsers")}
        </TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={userTableData}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          sx={tableSx}
        />
      </TableBoxBody>
    </TableBox>
  );
};

interface FeatureFlagEditConfirmationDialogProps {
  featureFlagId: string;
  featureFlagName: string;
  statusType: InternalFeatureFlagEnabledStatusType;
}

const FeatureFlagEditConfirmationDialog: React.FC<
  ConfirmationDialogProps<string, FeatureFlagEditConfirmationDialogProps>
> = ({
  alertProps,
  onSuccess,
  onCancel,
  extraProps: { featureFlagId, featureFlagName, statusType },
}) => {
  const { t } = useTranslation(["enums", "internal"]);

  return (
    <Modal scrollBehavior="inside" {...alertProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {featureFlagName}
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <Box mb={4}>
            <Text textStyle="bodyBold">
              {t("internal:featureFlagPage.edit.warningText")}
            </Text>
          </Box>
          <Descriptions>
            <Descriptions.Item
              label={t("internal:featureFlagPage.enableFeatureFlag")}
            >
              {t(`enums:FeatureFlagStatusTypeEnum.${statusType}`)}
            </Descriptions.Item>
          </Descriptions>
        </ModalBody>
        <ModalFooter gap={2}>
          <ButtonOrder
            dismiss={
              <Button colorScheme="secondary" onClick={() => onCancel()}>
                {t("common:cancel")}
              </Button>
            }
            confirm={
              <Button
                colorScheme="destructive"
                onClick={() => onSuccess(featureFlagId)}
              >
                {t("common:edit")}
              </Button>
            }
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const InternalFeatureFlagEditPage: React.FC = () => {
  const { t } = useTranslation(["enums", "internal"]);
  const { featureFlagId } = useRequiredStringParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data: featureFlagData } =
    useFeatureFlagRetrieveSuspense(featureFlagId);

  const { mutateAsync: editFeatureFlag } = useInternalFeatureFlagUpdate({
    mutation: {
      onMutate: () =>
        showToast({
          message: t("internal:featureFlagPage.edit.success"),
          variant: "info",
        }),
      onSuccess: async () => {
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: getFeatureFlagRetrieveQueryKey(featureFlagId),
          }),
          queryClient.invalidateQueries({
            queryKey:
              getInternalFeatureFlagCustomerTableDataRetrieveQueryKey(
                featureFlagId,
              ),
          }),
          queryClient.invalidateQueries({
            queryKey:
              getInternalFeatureFlagUserTableDataRetrieveQueryKey(
                featureFlagId,
              ),
          }),
        ]);
      },
    },
  });

  const featureFlagName = naiveFormatEnumValue(
    get(featureFlagData, "name", ""),
  );

  const customerIds = useMemo(
    () =>
      featureFlagData.customer_ids
        ? Object.fromEntries(
            featureFlagData.customer_ids.map((customer_id) => [
              customer_id,
              true,
            ]),
          )
        : {},
    [featureFlagData],
  );
  const userIds = useMemo(
    () =>
      featureFlagData.user_ids
        ? Object.fromEntries(
            featureFlagData.user_ids.map((user_id) => [user_id, true]),
          )
        : {},
    [featureFlagData],
  );

  const [userRowSelection, setUserRowSelection] =
    useState<RowSelectionState>(userIds);
  const [customerRowSelection, setCustomerRowSelection] =
    useState<RowSelectionState>(customerIds);

  const form = useForm<Pick<InternalFeatureFlagEditDataRequest, "status_type">>(
    {
      defaultValues: {
        status_type: getFeatureFlagStatusType(featureFlagData),
      },
    },
  );

  const statusType = form.watch("status_type");
  const isCustom = statusType === "custom";

  const selectFeatureFlagForEdit = useConfirmationDialog(
    FeatureFlagEditConfirmationDialog,
    {
      featureFlagId,
      featureFlagName,
      statusType,
    },
  );

  const onSubmit = async (
    data: Pick<InternalFeatureFlagEditDataRequest, "status_type">,
  ) => {
    const result = await selectFeatureFlagForEdit();
    if (result == featureFlagId) {
      const editData: InternalFeatureFlagEditDataRequest = {
        id: featureFlagId,
        status_type: data.status_type,
        customer_ids: customerRowSelection,
        user_ids: userRowSelection,
      };

      await editFeatureFlag({ featureFlagId, data: editData });
    }

    return result;
  };

  return (
    <PageContent>
      <PageHeader
        title={featureFlagName}
        backButton={{
          title: t("common:back"),
          path: rootRoute({})
            .internal({})
            .featureFlag({ featureFlagId })
            .details({}).$,
        }}
        helperText={t("internal:featureFlagPage.helperText")}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={(result) => {
            if (result == featureFlagId) {
              navigate(
                rootRoute({})
                  .internal({})
                  .featureFlag({ featureFlagId })
                  .details({}).$,
              );
            }
          }}
        >
          <ContentBlockGroup>
            <ContentBlock>
              <ContentBlockBody variant="form">
                <FormToggleButtonGroup
                  name="status_type"
                  title={t("internal:featureFlagPage.enableFeatureFlag")}
                >
                  <ToggleButton value={FeatureFlagStatusTypeEnum.environment}>
                    {t(
                      `enums:FeatureFlagStatusTypeEnum.${FeatureFlagStatusTypeEnum.environment}`,
                    )}
                  </ToggleButton>
                  <ToggleButton value={FeatureFlagStatusTypeEnum.custom}>
                    {t(
                      `enums:FeatureFlagStatusTypeEnum.${FeatureFlagStatusTypeEnum.custom}`,
                    )}
                  </ToggleButton>
                  <ToggleButton value={FeatureFlagStatusTypeEnum.none}>
                    {t(
                      `enums:FeatureFlagStatusTypeEnum.${FeatureFlagStatusTypeEnum.none}`,
                    )}
                  </ToggleButton>
                </FormToggleButtonGroup>
                <FormFooter>
                  <FormFooterActions>
                    <FormCancelButton
                      onClick={() =>
                        navigate(
                          rootRoute({})
                            .internal({})
                            .featureFlag({ featureFlagId })
                            .details({}).$,
                        )
                      }
                    />
                    <FormSubmitButton colorScheme="destructive">
                      {t("common:edit")}
                    </FormSubmitButton>
                  </FormFooterActions>
                </FormFooter>
              </ContentBlockBody>
            </ContentBlock>
          </ContentBlockGroup>
          {isCustom && (
            <>
              <InternalFeatureFlagCustomerTable
                featureFlagId={featureFlagId}
                rowSelection={customerRowSelection}
                setRowSelection={setCustomerRowSelection}
              />
              <InternalFeatureFlagUserTable
                featureFlagId={featureFlagId}
                rowSelection={userRowSelection}
                setRowSelection={setUserRowSelection}
              />
            </>
          )}
        </Form>
      </PageBody>
    </PageContent>
  );
};
